import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@iptv/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { adminFetch } from '../lib/api';

/**
 * Operator metrics page. First panel: bouquet rollup over a sliding
 * window (7 / 30 / 90 days). Easy to extend with more panels later
 * (orders/day, top plans, conversion rate, etc.) — they'd be sibling
 * <Card>s under the same heading.
 *
 * The bouquet code → friendly label map is duplicated from LeadsPage on
 * purpose: each page owns its own copy so they evolve independently. If
 * a third consumer shows up, we'll extract it to a shared module.
 */

interface BouquetCount {
  code: string;
  count: number;
}

interface BouquetMetrics {
  window_days: number;
  since: string;
  until: string;
  total_leads: number;
  engaged_leads: number;
  counts: BouquetCount[];
}

interface SiteRef {
  id: number;
  slug: string;
}

const BOUQUET_LABELS: Record<string, string> = {
  // New codes (post-2026-05-16 rework)
  worldwide: 'Worldwide',
  us: 'US',
  latino: 'Latino',
  canadian: 'Canadian',
  world_cup: 'World Cup',
  local: 'Local channels',
  // Legacy codes — kept so historical aggregations still label cleanly
  global: 'Global (legacy)',
  'us-sports': 'US sports (legacy)',
  kids: 'Kids (legacy)',
  wc26: 'World Cup 2026 (legacy)',
  hispano: 'Hispano (legacy)',
};

const WINDOWS = [7, 30, 90] as const;

export function MetricsPage() {
  const [windowDays, setWindowDays] = useState<(typeof WINDOWS)[number]>(30);
  const [siteId, setSiteId] = useState<string>('');

  const sites = useQuery({
    queryKey: ['admin', 'sites'],
    queryFn: () => adminFetch<SiteRef[]>('/api/v1/admin/sites'),
  });

  const metrics = useQuery({
    queryKey: ['admin', 'metrics', 'bouquets', windowDays, siteId],
    queryFn: () =>
      adminFetch<BouquetMetrics>('/api/v1/admin/metrics/bouquets', {
        query: {
          window_days: windowDays,
          site_source_id: siteId || undefined,
        },
      }),
  });

  // Same `?.` hop-by-hop trap as AppLayout: `metrics.data?.counts.map(...)`
  // would still call `.map` on `undefined` if the response shape is
  // missing `counts`. Repeat the `?.` so the chain short-circuits at
  // every hop, then `?? []` provides the empty-spread fallback.
  const maxCount = Math.max(1, ...(metrics.data?.counts?.map((c) => c.count) ?? []));
  const engagedPct = metrics.data?.total_leads
    ? Math.round((metrics.data.engaged_leads / metrics.data.total_leads) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-muted-foreground">Window:</span>
        {WINDOWS.map((d) => (
          <Button
            key={d}
            size="sm"
            variant={d === windowDays ? 'default' : 'outline'}
            onClick={() => setWindowDays(d)}
          >
            {d}d
          </Button>
        ))}
        <span className="ml-3 text-sm text-muted-foreground">Site:</span>
        <select
          value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
          className="rounded-md border bg-background px-2 py-1 text-sm"
        >
          <option value="">All sites</option>
          {sites.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.slug}
            </option>
          ))}
        </select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bouquet demand</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {metrics.isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
          {metrics.data && (
            <>
              <div className="grid gap-3 text-sm md:grid-cols-3">
                <StatTile label="Total leads" value={metrics.data.total_leads.toLocaleString()} />
                <StatTile
                  label="Engaged (picked ≥ 1 bouquet)"
                  value={`${metrics.data.engaged_leads.toLocaleString()} (${engagedPct}%)`}
                />
                <StatTile label="Distinct codes" value={metrics.data.counts.length.toString()} />
              </div>

              {metrics.data.counts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No bouquets selected in the last {windowDays} days.
                </p>
              ) : (
                <ul className="space-y-2">
                  {metrics.data.counts.map((c) => {
                    const label = BOUQUET_LABELS[c.code] ?? c.code;
                    const isLegacy = label.includes('(legacy)');
                    const widthPct = Math.max(2, Math.round((c.count / maxCount) * 100));
                    return (
                      <li key={c.code} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <span className="font-medium">{label}</span>
                            {isLegacy && (
                              <Badge variant="secondary" className="text-[10px]">
                                legacy
                              </Badge>
                            )}
                            <span className="text-xs text-muted-foreground">{c.code}</span>
                          </span>
                          <span className="font-mono text-sm">{c.count.toLocaleString()}</span>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${widthPct}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <p className="pt-2 text-[11px] text-muted-foreground">
                Window: {new Date(metrics.data.since).toLocaleDateString()} →{' '}
                {new Date(metrics.data.until).toLocaleDateString()}
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}
